// /password-reset/confirm?token=... — set a new password.
//
// The backend validates the token + the password strength server-side.
// On success the customer is logged out everywhere (refresh families
// revoked) — we route them to /login so they sign in with the new
// password, which is the safest UX (don't auto-login a session whose
// refresh-token state just got invalidated).

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';

export function PasswordResetConfirm(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);

  if (!token) return <Navigate to="/password-reset" replace />;

  return (
    <div className="card">
      <h2>{t('passwordResetConfirm.title')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMessage(null);
          setSuggestions(null);
          if (password !== confirm) {
            setErrorMessage(t('passwordResetConfirm.errors.mismatch'));
            return;
          }
          setSubmitting(true);
          void (async () => {
            try {
              await authApi.confirmPasswordReset(token, password);
              navigate('/login', {
                replace: true,
                state: { resetMessage: t('passwordResetConfirm.successMessage') },
              });
            } catch (err) {
              const envelope = extractApiError(err);
              setErrorMessage(envelope?.message ?? t('passwordResetConfirm.errors.updateFailed'));
              const sugg = envelope?.details?.suggestions;
              if (Array.isArray(sugg)) setSuggestions(sugg as string[]);
              setSubmitting(false);
            }
          })();
        }}
      >
        <div className="form-row">
          <label htmlFor="password">{t('passwordResetConfirm.fields.newPassword.label')}</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="help">{t('passwordResetConfirm.fields.newPassword.help')}</span>
        </div>
        <div className="form-row">
          <label htmlFor="confirm">{t('passwordResetConfirm.fields.confirmPassword.label')}</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {errorMessage && (
          <div className="banner banner--error">
            <strong>{errorMessage}</strong>
            {suggestions && suggestions.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {suggestions.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button className="primary" type="submit" disabled={submitting}>
          {submitting
            ? t('passwordResetConfirm.actions.submitting')
            : t('passwordResetConfirm.actions.submit')}
        </button>
        <p style={{ marginTop: 16 }}>
          <Trans
            i18nKey="passwordResetConfirm.expiredLink"
            t={t}
            components={[<Link key="reset" to="/password-reset" />]}
          />
        </p>
      </form>
    </div>
  );
}
